import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, CheckCircle2, Search } from 'lucide-react';
import { toast } from 'sonner';

const API = window.location.origin;

const INDUSTRY_ROLES = [
  { value: 'owner', label: 'Owner' },
  { value: 'trainer', label: 'Trainer' },
  { value: 'agent', label: 'Agent' },
  { value: 'farm_manager', label: 'Farm Manager' },
  { value: 'vet', label: 'Veterinarian' },
  { value: 'consignor', label: 'Consignor' },
  { value: 'syndicate_member', label: 'Syndicate Member' },
  { value: 'other', label: 'Other' },
];

export default function KioskPage() {
  const { saleId, token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [kioskData, setKioskData] = useState(null);
  const [searchHip, setSearchHip] = useState('');
  const [form, setForm] = useState({ first_name: '', last_name: '', industry_role: '', session_dates: [] });
  const [selectedHips, setSelectedHips] = useState({});

  useEffect(() => {
    fetch(`${API}/api/kiosk/public/${saleId}/${token}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(setKioskData)
      .catch(() => setKioskData(null))
      .finally(() => setLoading(false));
  }, [saleId, token]);

  const toggleHip = (hip) => {
    setSelectedHips(prev => {
      const key = hip.hip_number;
      return prev[key] ? (({ [key]: _, ...rest }) => rest)(prev) : { ...prev, [key]: { ...hip } };
    });
  };

  const selectByFilter = (filterFn) => {
    const filtered = {};
    (kioskData?.hips || []).filter(filterFn).forEach(h => { filtered[h.hip_number] = { ...h }; });
    setSelectedHips(prev => ({ ...prev, ...filtered }));
  };

  const toggleSession = (name) => {
    setForm(prev => ({
      ...prev,
      session_dates: prev.session_dates.includes(name)
        ? prev.session_dates.filter(s => s !== name)
        : [...prev.session_dates, name]
    }));
  };

  const handleSubmit = async () => {
    if (!form.first_name.trim()) { toast.error('First name is required'); return; }
    if (!form.last_name.trim()) { toast.error('Last name is required'); return; }
    if (!form.industry_role) { toast.error('Industry role is required'); return; }
    setSubmitting(true);
    try {
      const hips = Object.values(selectedHips).map(h => ({ hip_number: h.hip_number, opportunity_id: h.opportunity_id }));
      const r = await fetch(`${API}/api/kiosk/public/${saleId}/${token}/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, selected_hips: hips }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      setSubmitted(true);
    } catch (e) { toast.error(e.message || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const handleReset = () => {
    setForm({ first_name: '', last_name: '', industry_role: '', session_dates: [] });
    setSelectedHips({});
    setSubmitted(false);
    setSearchHip('');
  };

  const rawLogoUrl = kioskData?.tenant_logo_url || '';
  const logoSrc = rawLogoUrl
    ? (rawLogoUrl.startsWith('http') ? rawLogoUrl : `${API}${rawLogoUrl.startsWith('/') ? '' : '/'}${rawLogoUrl}`)
    : null;

  if (loading) return (
    <div className="min-h-screen bg-secondary flex items-center justify-center">
      <Loader2 className="w-10 h-10 animate-spin text-primary" />
    </div>
  );

  if (!kioskData) return (
    <div className="min-h-screen bg-secondary flex items-center justify-center p-4">
      <Card className="max-w-sm w-full shadow-lg"><CardContent className="pt-8 pb-8 text-center">
        <p className="text-lg font-semibold text-red-600">Invalid Kiosk Link</p>
        <p className="text-sm text-muted-foreground mt-2">This link is no longer valid or has expired.</p>
      </CardContent></Card>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen bg-secondary flex flex-col items-center justify-center p-4">
      {logoSrc && <img src={logoSrc} alt="" className="h-20 mb-6 object-contain"
        onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
      <Card className="max-w-sm w-full shadow-lg"><CardContent className="pt-10 pb-10 text-center">
        <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto mb-4" />
        <h2 className="font-serif text-2xl font-bold mb-3">Thank You!</h2>
        <p className="text-base text-muted-foreground mb-8">Thank you for submitting your card. You have been added to the Queue.</p>
        <Button onClick={handleReset} className="w-full h-12 text-base bg-primary hover:bg-primary/90 text-white uppercase tracking-wider">
          Submit Another Card
        </Button>
      </CardContent></Card>
    </div>
  );

  const hips = kioskData.hips || [];
  const filteredHips = hips.filter(h => !searchHip || String(h.hip_number).includes(searchHip));
  const hasColts = hips.some(h => h.gender === 'Colt');
  const hasFillies = hips.some(h => h.gender === 'Filly');
  const hasYearlings = hips.some(h => h.age_category === 'yearling');
  const hasWeanlings = hips.some(h => h.age_category === 'weanling');
  const canSubmit = form.first_name.trim() && form.last_name.trim() && form.industry_role;

  return (
    <div className="min-h-screen bg-secondary flex flex-col items-center py-8 px-4">
      {/* Logo */}
      {logoSrc && <img src={logoSrc} alt={kioskData.tenant_name} className="h-24 mb-4 object-contain"
        onError={(e) => { e.currentTarget.style.display = 'none'; }} />}

      {/* Sale Name */}
      <h1 className="font-serif text-3xl font-bold text-center mb-1">{kioskData.sale_name}</h1>
      <p className="text-muted-foreground text-center mb-6">Submit Your Viewing Card</p>

      <Card className="w-full max-w-lg shadow-lg">
        <CardContent className="p-6 space-y-5">
          {/* Viewer Info */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm">Your Information</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">First Name *</Label>
                <Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder="First name" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Last Name *</Label>
                <Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder="Last name" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Industry Role *</Label>
              <Select value={form.industry_role} onValueChange={v => setForm({ ...form, industry_role: v })}>
                <SelectTrigger><SelectValue placeholder="Select your role" /></SelectTrigger>
                <SelectContent>
                  {INDUSTRY_ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Sessions */}
          {kioskData.sessions?.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-semibold text-sm">Session</h3>
              <div className="flex flex-wrap gap-2">
                {kioskData.sessions.map(s => (
                  <label key={s.id} className="flex items-center gap-1.5 text-sm cursor-pointer border rounded px-2.5 py-1.5 hover:bg-muted/30 transition-colors select-none">
                    <input
                      type="checkbox"
                      checked={form.session_dates.includes(s.name)}
                      onChange={() => toggleSession(s.name)}
                      className="w-4 h-4 accent-primary"
                    />
                    {s.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Hip Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm">Select Hip Numbers</h3>
              <span className="text-xs text-muted-foreground">{Object.keys(selectedHips).length} selected</span>
            </div>

            {/* Bulk select buttons */}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="h-7 text-xs bg-white" onClick={() => selectByFilter(() => true)}>
                All Show
              </Button>
              {hasColts && (
                <Button size="sm" variant="outline" className="h-7 text-xs bg-white" onClick={() => selectByFilter(h => h.gender === 'Colt')}>
                  All Colts
                </Button>
              )}
              {hasFillies && (
                <Button size="sm" variant="outline" className="h-7 text-xs bg-white" onClick={() => selectByFilter(h => h.gender === 'Filly')}>
                  All Fillies
                </Button>
              )}
              {hasYearlings && (
                <Button size="sm" variant="outline" className="h-7 text-xs bg-white" onClick={() => selectByFilter(h => h.age_category === 'yearling')}>
                  All Yearlings
                </Button>
              )}
              {hasWeanlings && (
                <Button size="sm" variant="outline" className="h-7 text-xs bg-white" onClick={() => selectByFilter(h => h.age_category === 'weanling')}>
                  All Weanlings
                </Button>
              )}
              {Object.keys(selectedHips).length > 0 && (
                <Button size="sm" variant="outline" className="h-7 text-xs bg-white" onClick={() => setSelectedHips({})}>
                  Clear
                </Button>
              )}
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search hip #..." value={searchHip} onChange={e => setSearchHip(e.target.value)} className="pl-9 h-9" />
            </div>
            <div className="max-h-60 overflow-y-auto border rounded" style={{ isolation: 'isolate' }}>
              {filteredHips.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No hips found</p>
              ) : filteredHips.map(hip => {
                const isSelected = !!selectedHips[hip.hip_number];
                const hipNum = String(hip.hip_number).replace(/^(hip\s*#?\s*|#)/i, '').trim();
                return (
                  <label
                    key={hip.opportunity_id || hip.hip_number}
                    htmlFor={`hip-${hip.opportunity_id || hip.hip_number}`}
                    className={`flex items-center gap-3 px-3 py-2.5 border-b last:border-b-0 cursor-pointer select-none ${isSelected ? 'bg-green-50' : 'hover:bg-muted/30'}`}
                  >
                    <input
                      type="checkbox"
                      id={`hip-${hip.opportunity_id || hip.hip_number}`}
                      checked={isSelected}
                      onChange={() => toggleHip(hip)}
                      className="flex-shrink-0 w-4 h-4 accent-primary"
                    />
                    <span className="text-sm font-medium select-none">Hip #{hipNum}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Submit */}
          <div
            onClick={() => { if (!submitting && canSubmit) handleSubmit(); }}
            className={`w-full h-12 flex items-center justify-center text-base text-white uppercase tracking-wider font-semibold rounded-md select-none bg-primary ${(!canSubmit || submitting) ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            role="button"
            data-testid="kiosk-submit"
          >
            {submitting ? 'Submitting...' : 'SUBMIT CARD'}
          </div>
        </CardContent>
      </Card>

      {/* Powered by Green Door */}
      <div className="mt-6 flex justify-center">
        <img src={`${API}/api/brand/powered-by-footer.png`} alt="Powered by Green Door" className="h-16 object-contain opacity-70" 
          onError={(e) => { e.target.style.display = 'none'; }} />
      </div>
    </div>
  );
}
